import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from "@angular/core";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { LucideDynamicIcon } from "@lucide/angular";
import { backend } from "@/integrations/backend/client";
import { AuthService, User } from "../core/auth.service";
import { LanguageCode } from "../core/language.model";
import { LanguageService } from "../core/language.service";
import { ToastService } from "../core/toast.service";
import { LanguageSelectorComponent } from "../shared/language-selector.component";
import { badgeClasses, buttonClasses, cardClasses } from "../shared/ui";

type LocalizedText = Record<LanguageCode, string>;

interface TutorialStep {
  icon: string;
  title: LocalizedText;
  summary: LocalizedText;
  rules: LocalizedText[];
}

interface TutorialCopy {
  backToMap: string;
  completed: string;
  required: string;
  headerTitle: string;
  headerSubtitle: string;
  badgeLabel: string;
  heroTitle: string;
  heroDescription: string;
  progress: string;
  readInstruction: string;
  stepsAria: string;
  stepLabel: string;
  back: string;
  next: string;
  unlocking: string;
  completeAction: string;
  completedToast: string;
  completeError: string;
}

const TUTORIAL_COPY: Record<LanguageCode, TutorialCopy> = {
  en: {
    backToMap: "Back to map",
    completed: "Completed",
    required: "Required",
    headerTitle: "LINA First Steps",
    headerSubtitle: "Learn the rules before entering the islands.",
    badgeLabel: "Game tutorial",
    heroTitle: "Welcome to your learning expedition",
    heroDescription:
      "Complete these short steps to understand scoring, stages, streaks, crews, the victory gate, leaderboards and island items. After that, the game will unlock for your account.",
    progress: "Progress",
    readInstruction:
      "Read every rule group, then confirm that you are ready to start.",
    stepsAria: "Tutorial steps",
    stepLabel: "Step {step}",
    back: "Back",
    next: "Next",
    unlocking: "Unlocking...",
    completeAction: "I understand. Start playing",
    completedToast: "Tutorial completed. The islands are ready.",
    completeError: "Could not complete the tutorial. Please try again.",
  },
  pt: {
    backToMap: "Voltar ao mapa",
    completed: "Concluido",
    required: "Obrigatorio",
    headerTitle: "Primeiros passos da LINA",
    headerSubtitle: "Aprenda as regras antes de entrar nas ilhas.",
    badgeLabel: "Tutorial do jogo",
    heroTitle: "Bem-vindo a sua expedicao de aprendizado",
    heroDescription:
      "Complete estes passos curtos para entender pontuacao, fases, sequencias, crews, portao de vitoria, rankings e itens da ilha. Depois disso, o jogo sera liberado para sua conta.",
    progress: "Progresso",
    readInstruction:
      "Leia cada grupo de regras e confirme quando estiver pronto para comecar.",
    stepsAria: "Etapas do tutorial",
    stepLabel: "Etapa {step}",
    back: "Voltar",
    next: "Proximo",
    unlocking: "Liberando...",
    completeAction: "Entendi. Comecar a jogar",
    completedToast: "Tutorial concluido. As ilhas estao prontas.",
    completeError: "Nao foi possivel concluir o tutorial. Tente novamente.",
  },
  nl: {
    backToMap: "Terug naar kaart",
    completed: "Voltooid",
    required: "Verplicht",
    headerTitle: "LINA Eerste stappen",
    headerSubtitle: "Leer de regels voordat je de eilanden betreedt.",
    badgeLabel: "Speltutorial",
    heroTitle: "Welkom bij je leerexpeditie",
    heroDescription:
      "Voltooi deze korte stappen om scores, fases, streaks, crews, de overwinningspoort, ranglijsten en eilanditems te begrijpen. Daarna wordt het spel voor je account ontgrendeld.",
    progress: "Voortgang",
    readInstruction:
      "Lees elke regelgroep en bevestig daarna dat je klaar bent om te starten.",
    stepsAria: "Tutorialstappen",
    stepLabel: "Stap {step}",
    back: "Terug",
    next: "Volgende",
    unlocking: "Ontgrendelen...",
    completeAction: "Ik begrijp het. Start het spel",
    completedToast: "Tutorial voltooid. De eilanden zijn klaar.",
    completeError: "De tutorial kon niet worden voltooid. Probeer het opnieuw.",
  },
  sv: {
    backToMap: "Tillbaka till kartan",
    completed: "Klar",
    required: "Krav",
    headerTitle: "LINAs första steg",
    headerSubtitle: "Lär dig reglerna innan du går in på öarna.",
    badgeLabel: "Spelhandledning",
    heroTitle: "Välkommen till din läranderesa",
    heroDescription:
      "Gå igenom de här korta stegen för att förstå poäng, steg, streaks, crews, segerporten, topplistor och öföremål. Därefter låses spelet upp för ditt konto.",
    progress: "Framsteg",
    readInstruction:
      "Läs varje regelgrupp och bekräfta sedan att du är redo att börja.",
    stepsAria: "Handledningssteg",
    stepLabel: "Steg {step}",
    back: "Tillbaka",
    next: "Nästa",
    unlocking: "Låser upp...",
    completeAction: "Jag förstår. Börja spela",
    completedToast: "Handledningen är klar. Öarna är redo.",
    completeError: "Det gick inte att slutföra handledningen. Försök igen.",
  },
};

const TUTORIAL_STEPS: TutorialStep[] = [
  {
    icon: "flag",
    title: {
      en: "Profile and hub",
      pt: "Perfil e hub",
      nl: "Profiel en hub",
      sv: "Profil och hubb",
    },
    summary: {
      en: "Your account belongs to one hub, and that hub is used for country boards and identity in the map.",
      pt: "Sua conta pertence a um hub, e esse hub e usado nos rankings por pais e na identidade do mapa.",
      nl: "Je account hoort bij één hub. Die hub wordt gebruikt voor landenranglijsten en je identiteit op de kaart.",
      sv: "Ditt konto tillhör en hubb, och den hubben används för landstopplistor och identitet på kartan.",
    },
    rules: [
      {
        en: "Choose one hub during signup: Netherlands, Sweden, Brasil or China.",
        pt: "Escolha um hub no cadastro: Netherlands, Sweden, Brasil ou China.",
        nl: "Kies één hub tijdens registratie: Netherlands, Sweden, Brasil of China.",
        sv: "Välj en hubb vid registrering: Netherlands, Sweden, Brasil eller China.",
      },
      {
        en: "Your hub flag appears beside your name after login and in leaderboard views.",
        pt: "A bandeira do seu hub aparece ao lado do seu nome apos o login e nos rankings.",
        nl: "De vlag van je hub verschijnt naast je naam na het inloggen en in ranglijsten.",
        sv: "Din hubbflagga visas bredvid ditt namn efter inloggning och på topplistor.",
      },
      {
        en: "You can update profile details in Settings, but leaderboard history always uses real player progress.",
        pt: "Voce pode atualizar detalhes do perfil em Configuracoes, mas o historico dos rankings sempre usa progresso real do jogador.",
        nl: "Je kunt profielgegevens aanpassen in Instellingen, maar ranglijstgeschiedenis gebruikt altijd echte spelervoortgang.",
        sv: "Du kan uppdatera profildetaljer i Inställningar, men topplistor använder alltid verkliga spelarframsteg.",
      },
    ],
  },
  {
    icon: "clipboard-list",
    title: {
      en: "Self assessment",
      pt: "Autoavaliacao",
      nl: "Zelfbeoordeling",
      sv: "Självskattning",
    },
    summary: {
      en: "The first assessment calibrates your starting point. The final reassessment unlocks only after the last island.",
      pt: "A primeira avaliacao calibra seu ponto de partida. A reavaliacao final so libera depois da ultima ilha.",
      nl: "De eerste beoordeling kalibreert je startpunt. De laatste herbeoordeling wordt pas na het laatste eiland ontgrendeld.",
      sv: "Den första självskattningen kalibrerar din startpunkt. Den slutliga självskattningen låses upp först efter sista ön.",
    },
    rules: [
      {
        en: "Complete the first self assessment once after this tutorial if your account has not done it yet.",
        pt: "Complete a primeira autoavaliacao uma vez depois deste tutorial, caso sua conta ainda nao tenha feito isso.",
        nl: "Voltooi de eerste zelfbeoordeling één keer na deze tutorial als je account dat nog niet heeft gedaan.",
        sv: "Gör den första självskattningen en gång efter den här handledningen om ditt konto inte redan har gjort den.",
      },
      {
        en: "The final reassessment button stays disabled until the last required island is complete.",
        pt: "O botao da reavaliacao final fica desabilitado ate a ultima ilha obrigatoria ser concluida.",
        nl: "De knop voor de laatste herbeoordeling blijft uitgeschakeld tot het laatste vereiste eiland is voltooid.",
        sv: "Knappen för den slutliga självskattningen är avstängd tills den sista nödvändiga ön är klar.",
      },
      {
        en: "The final reassessment is harder than the first one because it uses Stage 3 questions from the highest-complexity game content.",
        pt: "A reavaliacao final e mais dificil que a primeira porque usa questoes do Stage 3 do conteudo de maior complexidade do jogo.",
        nl: "De laatste herbeoordeling is moeilijker dan de eerste, omdat deze Stage 3-vragen uit de meest complexe gamecontent gebruikt.",
        sv: "Den slutliga sjalvskattningen ar svarare an den forsta eftersom den anvander Stage 3-fragor fran spelets mest komplexa innehall.",
      },
      {
        en: "At the final gate, retake the assessment and score 90% or higher to qualify for victory.",
        pt: "No portao final, refaca a avaliacao e tire 90% ou mais para se qualificar para a vitoria.",
        nl: "Bij de laatste poort doe je de beoordeling opnieuw en haal je 90% of hoger om voor de overwinning te kwalificeren.",
        sv: "Vid slutporten gör du om självskattningen och får 90% eller mer för att kvalificera dig för seger.",
      },
      {
        en: "After the first assessment is complete, your map becomes the main place to choose islands and stages.",
        pt: "Depois da primeira avaliacao, o mapa passa a ser o principal lugar para escolher ilhas e fases.",
        nl: "Na de eerste beoordeling wordt de kaart de hoofdplek om eilanden en fases te kiezen.",
        sv: "Efter den första självskattningen blir kartan huvudplatsen för att välja öar och steg.",
      },
    ],
  },
  {
    icon: "route",
    title: {
      en: "Islands and stages",
      pt: "Ilhas e fases",
      nl: "Eilanden en fases",
      sv: "Öar och steg",
    },
    summary: {
      en: "Each island has stages. Finishing all stages completes the island and unlocks the next route.",
      pt: "Cada ilha tem fases. Concluir todas as fases completa a ilha e libera a proxima rota.",
      nl: "Elk eiland heeft fases. Als je alle fases afrondt, is het eiland voltooid en wordt de volgende route ontgrendeld.",
      sv: "Varje ö har steg. När alla steg är klara är ön klar och nästa rutt låses upp.",
    },
    rules: [
      {
        en: "A completed stage cannot be played again; choose another available stage on the same island.",
        pt: "Uma fase concluida nao pode ser jogada novamente; escolha outra fase disponivel na mesma ilha.",
        nl: "Een voltooide fase kan niet opnieuw worden gespeeld; kies een andere beschikbare fase op hetzelfde eiland.",
        sv: "Ett avslutat steg kan inte spelas igen; välj ett annat tillgängligt steg på samma ö.",
      },
      {
        en: "You can move to the next island only after every stage in the current island is complete.",
        pt: "Voce so pode ir para a proxima ilha depois que todas as fases da ilha atual forem concluidas.",
        nl: "Je kunt pas naar het volgende eiland als elke fase op het huidige eiland is voltooid.",
        sv: "Du kan gå vidare till nästa ö först när alla steg på den aktuella ön är klara.",
      },
      {
        en: "Questions become harder as islands advance, and questions should not repeat from earlier islands.",
        pt: "As perguntas ficam mais dificeis conforme as ilhas avancam, e perguntas de ilhas anteriores nao devem se repetir.",
        nl: "Vragen worden moeilijker naarmate de eilanden vorderen en vragen uit eerdere eilanden horen niet te herhalen.",
        sv: "Frågorna blir svårare när öarna går vidare, och frågor från tidigare öar ska inte upprepas.",
      },
    ],
  },
  {
    icon: "target",
    title: {
      en: "Scoring and speed bonus",
      pt: "Pontuacao e bonus de velocidade",
      nl: "Score en snelheidsbonus",
      sv: "Poäng och snabbhetsbonus",
    },
    summary: {
      en: "Correct answers build your score, and fast correct answers can earn extra points.",
      pt: "Respostas corretas aumentam sua pontuacao, e respostas corretas rapidas podem render pontos extras.",
      nl: "Goede antwoorden verhogen je score, en snelle goede antwoorden kunnen extra punten opleveren.",
      sv: "Rätta svar bygger din poäng, och snabba rätta svar kan ge extra poäng.",
    },
    rules: [
      {
        en: "Correct answers add the question value to your player score.",
        pt: "Respostas corretas somam o valor da pergunta a sua pontuacao de jogador.",
        nl: "Goede antwoorden voegen de vraagwaarde toe aan je spelersscore.",
        sv: "Rätta svar lägger till frågans värde till din spelarpoäng.",
      },
      {
        en: "Fast correct answers can earn up to 3 bonus points: best answers in 5 seconds, then smaller bonuses up to 15 seconds.",
        pt: "Respostas corretas rapidas podem render ate 3 pontos bonus: melhor bonus em 5 segundos, depois bonus menores ate 15 segundos.",
        nl: "Snelle goede antwoorden kunnen tot 3 bonuspunten opleveren: de beste bonus binnen 5 seconden, daarna kleinere bonussen tot 15 seconden.",
        sv: "Snabba rätta svar kan ge upp till 3 bonuspoäng: bästa bonusen inom 5 sekunder, sedan mindre bonusar upp till 15 sekunder.",
      },
      {
        en: "Wrong answers do not grant points, but the explanation helps you learn before the next question.",
        pt: "Respostas erradas nao dao pontos, mas a explicacao ajuda voce a aprender antes da proxima pergunta.",
        nl: "Foute antwoorden geven geen punten, maar de uitleg helpt je leren voor de volgende vraag.",
        sv: "Fel svar ger inga poäng, men förklaringen hjälper dig att lära inför nästa fråga.",
      },
    ],
  },
  {
    icon: "flame",
    title: {
      en: "Daily streaks",
      pt: "Sequencias diarias",
      nl: "Dagelijkse streaks",
      sv: "Dagliga streaks",
    },
    summary: {
      en: "The flame streak rewards daily return visits.",
      pt: "A sequencia da chama recompensa acessos diarios.",
      nl: "De vlam-streak beloont dagelijkse terugkeer.",
      sv: "Flamstreaken belönar dagliga återbesök.",
    },
    rules: [
      {
        en: "Logging in on consecutive weekdays increases your daily streak.",
        pt: "Entrar em dias uteis consecutivos aumenta sua sequencia diaria.",
        nl: "Inloggen op opeenvolgende weekdagen verhoogt je dagelijkse streak.",
        sv: "Inloggning pa efterfoljande vardagar okar din dagliga streak.",
      },
      {
        en: "Saturdays and Sundays do not reset the streak, increase it, or grant streak bonus points.",
        pt: "Sabados e domingos nao reiniciam a sequencia, nao aumentam a ofensiva e nao dao bonus de ofensiva.",
        nl: "Zaterdagen en zondagen resetten de streak niet, verhogen hem niet en geven geen streakbonus.",
        sv: "Lordagar och sondagar nollstaller inte streaken, okar den inte och ger ingen streakbonus.",
      },
      {
        en: "Your current streak appears on the map with the flame indicator.",
        pt: "Sua sequencia atual aparece no mapa com o indicador de chama.",
        nl: "Je huidige streak verschijnt op de kaart met de vlamindicator.",
        sv: "Din aktuella streak visas på kartan med flamindikatorn.",
      },
    ],
  },
  {
    icon: "users",
    title: {
      en: "Crews",
      pt: "Crews",
      nl: "Crews",
      sv: "Crews",
    },
    summary: {
      en: "Crews are team groups, but they follow progression rules so everyone advances together.",
      pt: "Crews sao grupos de equipe, mas seguem regras de progressao para que todos avancem juntos.",
      nl: "Crews zijn teamgroepen, maar ze volgen voortgangsregels zodat iedereen samen vooruitgaat.",
      sv: "Crews är teamgrupper, men de följer framstegsregler så att alla går vidare tillsammans.",
    },
    rules: [
      {
        en: "Any player can create a crew and invite other players.",
        pt: "Qualquer jogador pode criar uma crew e convidar outros jogadores.",
        nl: "Elke speler kan een crew maken en andere spelers uitnodigen.",
        sv: "Alla spelare kan skapa en crew och bjuda in andra spelare.",
      },
      {
        en: "Invitations expire after 24 hours; after that, the captain must send a new invitation.",
        pt: "Convites expiram depois de 24 horas; depois disso, o capitao precisa enviar um novo convite.",
        nl: "Uitnodigingen verlopen na 24 uur; daarna moet de kapitein een nieuwe uitnodiging sturen.",
        sv: "Inbjudningar går ut efter 24 timmar; därefter måste kaptenen skicka en ny inbjudan.",
      },
      {
        en: "A crew can have 2 to 4 explorers.",
        pt: "Uma crew pode ter de 2 a 4 exploradores.",
        nl: "Een crew kan 2 tot 4 ontdekkers hebben.",
        sv: "En crew kan ha 2 till 4 utforskare.",
      },
      {
        en: "Crew members can come from any hub.",
        pt: "Membros da crew podem vir de qualquer hub.",
        nl: "Crewleden mogen uit elke hub komen.",
        sv: "Crewmedlemmar kan komma fran valfri hubb.",
      },
      {
        en: "More than one crew member can share the same hub.",
        pt: "Mais de um membro da crew pode ter o mesmo hub.",
        nl: "Meerdere crewleden mogen dezelfde hub hebben.",
        sv: "Flera crewmedlemmar kan ha samma hubb.",
      },
      {
        en: "A crew can include only players at the same island level.",
        pt: "Uma crew so pode incluir jogadores no mesmo nivel de ilha.",
        nl: "Een crew kan alleen spelers bevatten die op hetzelfde eilandniveau zitten.",
        sv: "En crew kan bara innehålla spelare på samma önivå.",
      },
      {
        en: "A crew advances only when every member has advanced from the required island.",
        pt: "Uma crew so avanca quando todos os membros avancaram da ilha exigida.",
        nl: "Een crew gaat pas verder wanneer elk lid vanaf het vereiste eiland is doorgeschoven.",
        sv: "En crew går vidare först när varje medlem har gått vidare från den krävda ön.",
      },
    ],
  },
  {
    icon: "crown",
    title: {
      en: "Victory gate",
      pt: "Portao de vitoria",
      nl: "Overwinningspoort",
      sv: "Segerport",
    },
    summary: {
      en: "The winner is declared only after the islands, final reassessment and victory declarations are complete.",
      pt: "O vencedor so e declarado depois que as ilhas, a reavaliacao final e as declaracoes de vitoria estiverem concluidas.",
      nl: "De winnaar wordt pas uitgeroepen nadat de eilanden, laatste herbeoordeling en overwinningsverklaringen klaar zijn.",
      sv: "Vinnaren utses först när öarna, den slutliga självskattningen och segerdeklarationerna är klara.",
    },
    rules: [
      {
        en: "Solo players can win by completing every required island, passing the final reassessment with 90% or higher and declaring victory.",
        pt: "Players solo podem vencer concluindo todas as ilhas obrigatorias, passando na reavaliacao final com 90% ou mais e declarando vitoria.",
        nl: "Solospelers kunnen winnen door elk vereist eiland te voltooien, de laatste herbeoordeling met 90% of hoger te halen en de overwinning te verklaren.",
        sv: "Solospelare kan vinna genom att slutföra varje nödvändig ö, klara den slutliga självskattningen med 90% eller mer och deklarera seger.",
      },
      {
        en: "For crews, every member must complete every required island, pass the final reassessment and declare victory one by one.",
        pt: "Para crews, todos os membros precisam concluir todas as ilhas obrigatorias, passar na reavaliacao final e declarar vitoria um por um.",
        nl: "Voor crews moet elk lid elk vereist eiland voltooien, de laatste herbeoordeling halen en één voor één de overwinning verklaren.",
        sv: "För crews måste varje medlem slutföra varje nödvändig ö, klara den slutliga självskattningen och deklarera seger en i taget.",
      },
      {
        en: "After victory, the winning crew is released in 10 days and members wait for the next game version while new islands are discovered.",
        pt: "Depois da vitoria, a crew vencedora e liberada em 10 dias e os membros aguardam a proxima versao do jogo enquanto novas ilhas sao descobertas.",
        nl: "Na de overwinning wordt de winnende crew na 10 dagen vrijgegeven en wachten leden op de volgende spelversie terwijl nieuwe eilanden worden ontdekt.",
        sv: "Efter segern släpps den vinnande crewn efter 10 dagar och medlemmarna väntar på nästa spelversion medan nya öar upptäcks.",
      },
    ],
  },
  {
    icon: "trophy",
    title: {
      en: "Leaderboards",
      pt: "Rankings",
      nl: "Ranglijsten",
      sv: "Topplistor",
    },
    summary: {
      en: "Boards rank real player progress globally, by hub and by crew.",
      pt: "Os rankings classificam progresso real dos jogadores globalmente, por hub e por crew.",
      nl: "Ranglijsten ordenen echte spelervoortgang wereldwijd, per hub en per crew.",
      sv: "Topplistor rankar verkliga spelarframsteg globalt, per hubb och per crew.",
    },
    rules: [
      {
        en: "Global, hub and crew leaderboards use real score data only.",
        pt: "Rankings globais, por hub e por crew usam apenas dados reais de pontuacao.",
        nl: "Wereldwijde, hub- en crewranglijsten gebruiken alleen echte scoregegevens.",
        sv: "Globala topplistor, hubbtopplistor och crewtopplistor använder bara verkliga poängdata.",
      },
      {
        en: "Country boards use the hub selected on signup.",
        pt: "Rankings por pais usam o hub escolhido no cadastro.",
        nl: "Landenranglijsten gebruiken de hub die bij registratie is gekozen.",
        sv: "Landstopplistor använder hubben som valdes vid registrering.",
      },
      {
        en: "Crew boards combine the score earned by crew members.",
        pt: "Rankings de crew combinam a pontuacao conquistada pelos membros.",
        nl: "Crewranglijsten combineren de score die crewleden hebben verdiend.",
        sv: "Crewtopplistor kombinerar poängen som crewmedlemmarna har tjänat.",
      },
    ],
  },
  {
    icon: "store",
    title: {
      en: "Island items",
      pt: "Itens da ilha",
      nl: "Eilanditems",
      sv: "Öföremål",
    },
    summary: {
      en: "Your island can change as you earn score and reach higher levels.",
      pt: "Sua ilha pode mudar conforme voce ganha pontos e alcanca niveis mais altos.",
      nl: "Je eiland kan veranderen terwijl je score verdient en hogere niveaus bereikt.",
      sv: "Din ö kan förändras när du tjänar poäng och når högre nivåer.",
    },
    rules: [
      {
        en: "Items unlock from your current level and total score.",
        pt: "Itens sao liberados pelo seu nivel atual e pela pontuacao total.",
        nl: "Items worden ontgrendeld door je huidige niveau en totale score.",
        sv: "Föremål låses upp genom din aktuella nivå och totalpoäng.",
      },
      {
        en: "Unlocked items can be added and positioned on land areas of the island.",
        pt: "Itens liberados podem ser adicionados e posicionados nas areas de terra da ilha.",
        nl: "Ontgrendelde items kunnen worden toegevoegd en geplaatst op landdelen van het eiland.",
        sv: "Upplåsta föremål kan läggas till och placeras på öns landområden.",
      },
      {
        en: "Animals and decorations may appear, move or change as your level and score grow.",
        pt: "Animais e decoracoes podem aparecer, se mover ou mudar conforme seu nivel e pontuacao crescem.",
        nl: "Dieren en decoraties kunnen verschijnen, bewegen of veranderen naarmate je niveau en score groeien.",
        sv: "Djur och dekorationer kan visas, röra sig eller förändras när din nivå och poäng ökar.",
      },
    ],
  },
];

function localized(text: LocalizedText, language: LanguageCode) {
  return text[language] || text.en;
}

@Component({
  selector: "app-tutorial-page",
  standalone: true,
  imports: [CommonModule, LanguageSelectorComponent, LucideDynamicIcon],
  template: `
    <div
      *ngIf="authLoading"
      class="min-h-screen flex items-center justify-center ocean-gradient"
    >
      <svg
        lucideIcon="loader-circle"
        class="h-8 w-8 animate-spin text-primary-foreground"
      ></svg>
    </div>

    <div
      *ngIf="!authLoading && user"
      class="min-h-screen bg-background bg-ocean-pattern"
    >
      <header
        class="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-md"
      >
        <div class="container mx-auto max-w-5xl px-4 py-4">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex min-w-0 items-center gap-3">
              <button
                *ngIf="onboardingCompleted"
                [class]="buttonClasses('ghost', 'icon')"
                (click)="router.navigateByUrl('/')"
                [title]="copy.backToMap"
              >
                <svg lucideIcon="arrow-left" class="h-5 w-5"></svg>
              </button>
              <div class="rounded-md bg-primary/10 p-2">
                <svg lucideIcon="compass" class="h-6 w-6 text-primary"></svg>
              </div>
              <div class="min-w-0">
                <h1 class="text-xl font-bold text-foreground">
                  {{ copy.headerTitle }}
                </h1>
                <p class="text-sm text-muted-foreground">
                  {{ copy.headerSubtitle }}
                </p>
              </div>
            </div>
            <div class="flex items-center gap-3">
              <app-language-selector
                [compact]="true"
                className="bg-background/80"
              />
              <span
                [class]="
                  badgeClasses(onboardingCompleted ? 'secondary' : 'outline')
                "
              >
                {{ onboardingCompleted ? copy.completed : copy.required }}
              </span>
            </div>
          </div>
        </div>
      </header>

      <main class="container mx-auto max-w-5xl px-4 py-8">
        <section class="mb-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_18rem]">
          <div>
            <span [class]="badgeClasses('secondary', 'gap-1')">
              <svg lucideIcon="book-open" class="h-3.5 w-3.5"></svg>
              {{ copy.badgeLabel }}
            </span>
            <h2
              class="mt-4 text-3xl font-bold leading-tight tracking-normal text-foreground md:text-4xl"
            >
              {{ copy.heroTitle }}
            </h2>
            <p class="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">
              {{ copy.heroDescription }}
            </p>
          </div>

          <aside [class]="cardClasses('p-4')">
            <div class="flex items-center justify-between text-sm">
              <span class="font-semibold text-foreground">{{
                copy.progress
              }}</span>
              <span class="text-muted-foreground"
                >{{ activeIndex + 1 }} / {{ steps.length }}</span
              >
            </div>
            <div class="mt-3 h-2 overflow-hidden rounded-full bg-muted">
              <div
                class="h-full rounded-full bg-primary transition-all"
                [style.width.%]="progressPercent"
              ></div>
            </div>
            <p class="mt-3 text-xs leading-5 text-muted-foreground">
              {{ copy.readInstruction }}
            </p>
          </aside>
        </section>

        <section class="grid gap-6 lg:grid-cols-[17rem_1fr]">
          <nav class="space-y-2" [attr.aria-label]="copy.stepsAria">
            <button
              *ngFor="let step of steps; let index = index"
              type="button"
              class="flex w-full items-center gap-3 rounded-md border px-3 py-3 text-left text-sm transition"
              [ngClass]="
                index === activeIndex
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-card text-muted-foreground hover:text-foreground'
              "
              (click)="activeIndex = index"
            >
              <svg [lucideIcon]="step.icon" class="h-4 w-4 shrink-0"></svg>
              <span class="min-w-0 truncate font-medium">{{
                localize(step.title)
              }}</span>
            </button>
          </nav>

          <article [class]="cardClasses('overflow-hidden')">
            <header class="border-b border-border p-6">
              <div class="flex items-start gap-4">
                <div class="rounded-md bg-primary/10 p-3">
                  <svg
                    [lucideIcon]="activeStep.icon"
                    class="h-7 w-7 text-primary"
                  ></svg>
                </div>
                <div class="min-w-0">
                  <p
                    class="text-xs font-semibold uppercase text-muted-foreground"
                  >
                    {{ stepLabel }}
                  </p>
                  <h3
                    class="mt-1 text-2xl font-semibold leading-tight tracking-normal text-foreground"
                  >
                    {{ localize(activeStep.title) }}
                  </h3>
                  <p class="mt-2 text-sm leading-6 text-muted-foreground">
                    {{ localize(activeStep.summary) }}
                  </p>
                </div>
              </div>
            </header>

            <div class="p-6">
              <ul class="grid gap-3">
                <li
                  *ngFor="let rule of activeStep.rules"
                  class="flex gap-3 rounded-md border border-border bg-muted/30 p-3 text-sm leading-6 text-foreground"
                >
                  <svg
                    lucideIcon="circle-check-big"
                    class="mt-1 h-4 w-4 shrink-0 text-accent"
                  ></svg>
                  <span>{{ localize(rule) }}</span>
                </li>
              </ul>
            </div>

            <footer
              class="flex flex-col gap-3 border-t border-border p-6 sm:flex-row sm:items-center sm:justify-between"
            >
              <button
                [class]="buttonClasses('outline')"
                [disabled]="activeIndex === 0 || completing"
                (click)="previousStep()"
              >
                <svg lucideIcon="arrow-left" class="h-4 w-4"></svg>
                {{ copy.back }}
              </button>

              <button
                *ngIf="!isLastStep"
                [class]="buttonClasses('default')"
                [disabled]="completing"
                (click)="nextStep()"
              >
                {{ copy.next }}
                <svg lucideIcon="arrow-right" class="h-4 w-4"></svg>
              </button>

              <button
                *ngIf="isLastStep"
                [class]="
                  buttonClasses(
                    'default',
                    'default',
                    'bg-primary text-primary-foreground'
                  )
                "
                [disabled]="completing"
                (click)="completeTutorial()"
              >
                <svg
                  *ngIf="completing"
                  lucideIcon="loader-circle"
                  class="h-4 w-4 animate-spin"
                ></svg>
                <svg
                  *ngIf="!completing"
                  lucideIcon="play"
                  class="h-4 w-4"
                ></svg>
                {{ completing ? copy.unlocking : copy.completeAction }}
              </button>
            </footer>
          </article>
        </section>
      </main>
    </div>
  `,
})
export class TutorialPageComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  readonly language = inject(LanguageService);
  private readonly changeDetector = inject(ChangeDetectorRef);

  authLoading = true;
  completing = false;
  onboardingCompleted = false;
  activeIndex = 0;
  user: User | null = null;
  private subscription?: Subscription;

  readonly steps = TUTORIAL_STEPS;

  ngOnInit() {
    this.subscription = this.auth.state$.subscribe((state) => {
      this.authLoading = state.loading;
      this.user = state.user;
      this.onboardingCompleted = Boolean(
        state.profile?.onboarding_completed_at,
      );
      this.changeDetector.detectChanges();

      if (!state.loading && !state.user) {
        void this.router.navigateByUrl("/auth");
      }
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  get copy() {
    return TUTORIAL_COPY[this.language.language] || TUTORIAL_COPY.en;
  }

  get activeStep() {
    return this.steps[this.activeIndex];
  }

  get isLastStep() {
    return this.activeIndex === this.steps.length - 1;
  }

  get progressPercent() {
    return ((this.activeIndex + 1) / this.steps.length) * 100;
  }

  get stepLabel() {
    return this.copy.stepLabel.replace("{step}", String(this.activeIndex + 1));
  }

  localize(text: LocalizedText) {
    return localized(text, this.language.language);
  }

  previousStep() {
    this.activeIndex = Math.max(0, this.activeIndex - 1);
  }

  nextStep() {
    this.activeIndex = Math.min(this.steps.length - 1, this.activeIndex + 1);
  }

  async completeTutorial() {
    if (!this.user) {
      await this.router.navigateByUrl("/auth");
      return;
    }

    this.completing = true;
    this.changeDetector.detectChanges();
    try {
      const { error } = await this.auth.updateProfile({
        onboarding_completed_at: new Date().toISOString(),
      });
      if (error) throw error;

      this.toast.success(this.copy.completedToast);
      await this.navigateAfterTutorial();
    } catch (error) {
      console.error("Error completing tutorial:", error);
      this.toast.error(this.copy.completeError);
    } finally {
      this.completing = false;
      this.changeDetector.detectChanges();
    }
  }

  private async navigateAfterTutorial() {
    if (!this.user) {
      await this.router.navigateByUrl("/auth");
      return;
    }

    const { data, error } = await backend
      .from("self_assessment_attempts")
      .select("id")
      .eq("user_id", this.user.id)
      .eq("assessment_type", "initial")
      .limit(1);
    if (error) throw error;

    await this.router.navigateByUrl(
      data && data.length > 0 ? "/" : "/self-assessment",
    );
  }

  buttonClasses = buttonClasses;
  cardClasses = cardClasses;
  badgeClasses = badgeClasses;
}
